// src/hooks/useRoutingRules.ts
//
// React data hook for the Settings → Routing Rules screen (Story 1.5). Plain
// useState/useCallback/useEffect per the repo's hook style (useRegions is the
// reference) — no React Query. All data access goes through
// routingRulesService. A save refetches rather than updating optimistically; a
// failed background refresh after a successful save is swallowed (it must not
// raise the page-level error banner and bait a re-submit — the 1.1 finding).

import { useState, useEffect, useCallback } from 'react'
import { routingRulesService } from '../services/routingRulesService'
import type { YakRoutingRule, YakRuleLayer } from '../types/yak'

interface UseRoutingRulesReturn {
  rules: YakRoutingRule[]
  positionCounts: Record<string, number>
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  saveLayers: (id: string, layers: YakRuleLayer[]) => Promise<void>
}

export function useRoutingRules(): UseRoutingRulesReturn {
  const [rules, setRules] = useState<YakRoutingRule[]>([])
  const [positionCounts, setPositionCounts] = useState<Record<string, number>>(
    {}
  )
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchRules = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      setError(null)
      const [nextRules, nextCounts] = await Promise.all([
        routingRulesService.listRules(),
        routingRulesService.listActivePositionCounts(),
      ])
      setRules(nextRules)
      setPositionCounts(nextCounts)
    } catch (err) {
      // A silent refresh runs after a save that already succeeded on the
      // server. Surfacing its failure as the page-level banner would
      // contradict that success and bait a re-submit. Only the initial/explicit
      // load owns the banner; a failed background refresh leaves the last-known
      // rules in place and reconciles on the next successful fetch.
      if (!silent) {
        setError(err instanceof Error ? err.message : 'Failed to load rules')
      } else {
        console.error('Routing rules refresh failed', err)
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRules()
  }, [fetchRules])

  const saveLayers = useCallback(
    async (id: string, layers: YakRuleLayer[]) => {
      await routingRulesService.updateLayers(id, layers)
      await fetchRules(true)
    },
    [fetchRules]
  )

  return {
    rules,
    positionCounts,
    loading,
    error,
    refetch: () => fetchRules(),
    saveLayers,
  }
}
